/**
 * QueryProfiler — profiles ONE statement and shows where its time goes.
 *
 * Shared by the query editor (statement under cursor) and Query Compare (side A
 * or B). Calls /api/tools/query-profile, which uses session profiling
 * (SET profiling=1 → SHOW PROFILE) — supported on the old-server fleet incl.
 * MariaDB 10.0, no server config needed. Profiling EXECUTES the statement, so a
 * non-SELECT is run exactly once and is gated behind a ⚠ confirm here.
 *
 * Props: { connectionId, database, sql, onClose }
 */
import { createSignal, createMemo, onMount, For, Show } from 'solid-js';
import Modal from './Modal.jsx';
import { profileQuery } from '../lib/queryprofile.js';
import { toast } from '../lib/toast.js';

const STAGE_COLORS = [
  '#3b82f6', '#f59e0b', '#10b981', '#ef4444', '#8b5cf6',
  '#06b6d4', '#ec4899', '#84cc16', '#f97316', '#6366f1',
];

const fmtMs = (ms) =>
  ms == null ? '—' : ms < 10 ? ms.toFixed(2) : ms < 100 ? ms.toFixed(1) : Math.round(ms).toLocaleString();

// A statement is "read-only" for profiling if it returns rows (so it's safe to
// run a few times). Anything else executes once, with a warning.
const isReadOnly = (sql) =>
  /^\s*(\(|select|show|desc|describe|explain|with|table|values)\b/i.test(sql || '');

export default function QueryProfiler(props) {
  const ro = createMemo(() => isReadOnly(props.sql));
  const [runs, setRuns] = createSignal(3);
  const [busy, setBusy] = createSignal(false);
  const [res, setRes] = createSignal(null);
  const [err, setErr] = createSignal('');
  const [ran, setRan] = createSignal(false);

  const run = async () => {
    setBusy(true); setErr(''); setRes(null);
    try {
      const r = await profileQuery(props.connectionId, props.database, props.sql, ro() ? runs() : 1);
      setRes(r);
      setRan(true);
      if (r.error) setErr(r.error);
    } catch (e) {
      setErr(e?.message || String(e));
    } finally {
      setBusy(false);
    }
  };

  // Auto-run read-only statements; writes wait for an explicit click (the warning).
  onMount(() => { if (ro()) run(); });

  const stages = () => res()?.stages || [];
  const stageTotal = () => res()?.stageSeconds || 0;
  const hottest = createMemo(() => {
    let max = -1;
    for (const s of stages()) if (s.seconds > max) max = s.seconds;
    return max;
  });
  // Table: stages ranked by cost (timeline below stays in execution order).
  const ranked = createMemo(() => [...stages()].sort((a, b) => b.seconds - a.seconds));
  const statusEntries = createMemo(() => Object.entries(res()?.statusDelta || {}).sort((a, b) => a[0].localeCompare(b[0])));
  const pct = (s) => (stageTotal() > 0 ? (s / stageTotal()) * 100 : 0);

  return (
    <Modal open={true} onClose={props.onClose} title="Query profiler" wide>
      <div class="qp-sql mono">{props.sql}</div>

      <Show when={!ro()}>
        <div class="qp-warn">
          ⚠ This isn't a SELECT — profiling <strong>executes</strong> it (once), which will modify data.
        </div>
      </Show>

      <div class="qp-controls">
        <Show when={ro()}>
          <label class="qp-runs">Runs
            <input class="input" type="number" min="1" max="10" value={runs()} disabled={busy()}
                   onInput={(e) => setRuns(Math.max(1, Math.min(10, Number(e.currentTarget.value) || 1)))} />
          </label>
        </Show>
        <button class="btn btn-primary btn-sm" disabled={busy()} onClick={run}>
          {busy() ? '⏳ Profiling…' : ran() ? '↻ Re-run' : (ro() ? '▶ Profile' : '⚠ Run & profile')}
        </button>
        <span class="spacer" />
        <Show when={res() && !err()}>
          <span class="muted" style="font-size:12px;">
            {fmtMs(res().wallMedian)} ms median{ro() && res().wallMs?.length > 1 ? ` · min ${fmtMs(res().wallMin)} · ${res().wallMs.length} runs` : ''} · {(res().rowCount ?? 0).toLocaleString()} rows
          </span>
        </Show>
      </div>

      <Show when={err()}><div class="form-error" style="margin:8px 0;">{err()}</div></Show>

      <Show when={res() && !err()}>
        {/* Stage timeline (execution order) */}
        <Show when={res().available} fallback={
          <div class="muted" style="font-size:12px;padding:8px 0;">{res().note || 'Per-stage profiling unavailable; timing + counters only.'}</div>
        }>
          <div class="qp-section-title">Where the time went — {fmtMs(stageTotal() * 1000)} ms across {stages().length} stages</div>
          <div class="qp-timeline">
            <For each={stages()}>
              {(s, i) => (
                <Show when={pct(s.seconds) > 0}>
                  <div class="qp-seg" classList={{ 'qp-seg-hot': s.seconds === hottest() }}
                       style={`width:${pct(s.seconds)}%;background:${STAGE_COLORS[i() % STAGE_COLORS.length]};`}
                       title={`${s.status} — ${fmtMs(s.seconds * 1000)} ms (${pct(s.seconds).toFixed(1)}%)`} />
                </Show>
              )}
            </For>
          </div>

          {/* Stage table (ranked by cost) */}
          <table class="qp-table">
            <thead><tr><th>Stage</th><th>Duration</th><th>%</th><th>CPU (u+s)</th><th>Block IO (in/out)</th></tr></thead>
            <tbody>
              <For each={ranked()}>
                {(s) => (
                  <tr classList={{ 'qp-row-hot': s.seconds === hottest() && s.seconds > 0 }}>
                    <td class="mono">{s.status}</td>
                    <td class="mono">{fmtMs(s.seconds * 1000)} ms</td>
                    <td class="mono">{pct(s.seconds).toFixed(1)}%</td>
                    <td class="mono">{s.cpuUser != null || s.cpuSys != null ? `${fmtMs((s.cpuUser || 0) * 1000)} + ${fmtMs((s.cpuSys || 0) * 1000)}` : <span class="muted">—</span>}</td>
                    <td class="mono">{s.blockIn != null || s.blockOut != null ? `${s.blockIn || 0} / ${s.blockOut || 0}` : <span class="muted">—</span>}</td>
                  </tr>
                )}
              </For>
            </tbody>
          </table>
        </Show>

        {/* Status-counter delta — the "why slow" signals */}
        <Show when={statusEntries().length > 0}>
          <div class="qp-section-title">Counters (one run)</div>
          <div class="qp-counters">
            <For each={statusEntries()}>
              {([k, v]) => (
                <span class="qp-counter" classList={{ 'qp-counter-warn': /disk|full_join|merge_passes/i.test(k) }}>
                  <span class="mono">{k}</span> <strong>{v.toLocaleString()}</strong>
                </span>
              )}
            </For>
          </div>
        </Show>

        {/* EXPLAIN (compact) */}
        <Show when={res().explain}>
          <div class="qp-section-title">EXPLAIN</div>
          <div class="qp-explain-wrap">
            <table class="qp-table">
              <thead><tr><For each={res().explain.columns}>{(c) => <th>{c}</th>}</For></tr></thead>
              <tbody>
                <For each={res().explain.rows}>
                  {(row) => <tr><For each={row}>{(cell) => <td class="mono">{cell}</td>}</For></tr>}
                </For>
              </tbody>
            </table>
          </div>
        </Show>
      </Show>
    </Modal>
  );
}
